
<template>
  <div v-if="show" class="modal-overlay" @click.self="closeModal">
    <div class="modal-container">
      <div class="modal-header">
        <h3>{{ step?.name || t('quarantine.timeline.stepDetail.title').value }}</h3>
        <button class="close-btn" @click="closeModal">×</button>
      </div>

      <div class="modal-content">
        <!-- 步骤信息 -->
        <div v-if="step" class="step-info-section">
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">{{ t('quarantine.timeline.stepDetail.day').value }}:</span>
              <span class="info-value">第 {{ step.day }} 天</span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ t('quarantine.timeline.stepDetail.type').value }}:</span>
              <span class="info-value">{{ getStepTypeLabel(step.type) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ t('quarantine.timeline.stepDetail.actions').value }}:</span>
              <span class="info-value">{{ step.actions.length }} {{ t('quarantine.timeline.stepDetail.actionsCount').value }}</span>
            </div>
          </div>

          <!-- 美化后的必做操作列表 -->
          <div v-if="step && step.actions && step.actions.length > 0" class="actions-section">
            <h4 class="section-title">📋 {{ t('quarantine.timeline.stepDetail.actions').value }}</h4>
            <div class="actions-list">
              <div
                v-for="(action, actionIndex) in step.actions"
                :key="actionIndex"
                class="action-item"
              >
                <div class="action-number">{{ actionIndex + 1 }}</div>
                <div class="action-content">
                  <span class="action-text">{{ action }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 药品信息 -->
          <div v-if="step && step.drugs && step.drugs.length > 0" class="medications-section">
            <h4>💊 {{ t('quarantine.timeline.stepDetail.medicationGuide').value }}</h4>
            <div class="medication-cards">
              <div
                v-for="(drug, index) in step!.drugs"
                :key="index"
                class="medication-card"
              >
                <div class="med-header">
                  <h5>{{ getMedicationName(drug.name) }}</h5>
                  <span class="target-ppm">
                    <span v-if="drug.dose_ml_per_L">{{ drug.dose_ml_per_L }} ml/L</span>
                    <span v-if="drug.dose_ml_per_10L">{{ drug.dose_ml_per_10L }} ml/10L</span>
                    <span v-if="drug.dose_mg_per_L">{{ drug.dose_mg_per_L }} mg/L</span>
                    <span v-if="drug.targetPPM">{{ drug.targetPPM }} ppm</span>
                    <span v-if="drug.tabletPerX_L">{{ drug.tabletPerX_L[0] }}片/{{ drug.tabletPerX_L[1] }}L</span>
                  </span>
                </div>
                
                <div class="dosage-calculation">
                  <div class="calculation-inputs">
                    <div class="input-group">
                      <label>{{ t('quarantine.timeline.stepDetail.waterVolumeLabel').value }}</label>
                      <input
                        type="number"
                        v-model="waterVolume"
                        class="form-control"
                        :placeholder="t('quarantine.timeline.stepDetail.waterVolumePlaceholder').value"
                        step="0.1"
                        min="1"
                      >
                      <div v-if="waterVolume <= 0" class="volume-warning">
                        {{ t('quarantine.timeline.stepDetail.waterVolumeWarning').value }}
                      </div>
                    </div>
                  </div>

                  <div class="calculation-result" v-if="waterVolume > 0">
                    <div class="result-item">
                      <span class="result-label">{{ t('quarantine.timeline.stepDetail.dosageLabel').value }}</span>
                      <span class="result-value">
                        {{ calculateDosage(drug) }} {{ getMedicationUnit(drug.name) }}
                      </span>
                    </div>
                    <div class="calculation-note">
                      {{ getCalculationNote(drug) }}
                    </div>
                  </div>
                </div>

                <div v-if="drug.note" class="med-notes">
                  <span class="notes-icon">📝</span>
                  <span class="notes-text">{{ drug.note }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 设备调整信息 -->
          <div v-if="step && step.equipmentAdjustments && step.equipmentAdjustments.length > 0" class="equipment-section">
            <h4>🔧 {{ t('quarantine.timeline.stepDetail.equipmentAdjustment').value }}</h4>
            <div class="equipment-list">
              <div
                v-for="(adjustment, index) in step!.equipmentAdjustments"
                :key="index"
                class="equipment-item"
              >
                <span class="equipment-icon">⚙️</span>
                <span class="equipment-text">{{ adjustment }}</span>
              </div>
            </div>
          </div>

          <!-- 警告信息 -->
          <div v-if="step && step.safetyNotes && step.safetyNotes.length > 0" class="warnings-section">
            <h4>⚠️ {{ t('quarantine.timeline.stepDetail.safetyNotes').value }}</h4>
            <div class="warning-list">
              <div
                v-for="(warning, index) in step!.safetyNotes"
                :key="index"
                class="warning-item"
              >
                <span class="warning-icon">⚠️</span>
                <span class="warning-text">{{ warning }}</span>
              </div>
            </div>
          </div>
        </div>


        <!-- 鱼只状态记录 -->
        <div class="fish-status-section">
          <h4>🐟 {{ t('quarantine.timeline.stepDetail.fishStatus').value }}</h4>
          <div class="form-row">
            <div class="input-group">
              <label>{{ t('quarantine.timeline.stepDetail.appetiteLabel').value }}</label>
              <select v-model="fishStatus.appetite" class="select-control">
                <option value="good">{{ t('quarantine.timeline.appetite.good').value }}</option>
                <option value="normal">{{ t('quarantine.timeline.appetite.normal').value }}</option>
                <option value="poor">{{ t('quarantine.timeline.appetite.poor').value }}</option>
                <option value="none">{{ t('quarantine.timeline.appetite.none').value }}</option>
              </select>
            </div>
            <div class="input-group">
              <label>{{ t('quarantine.timeline.stepDetail.breathingLabel').value }}</label>
              <select v-model="fishStatus.breathing" class="select-control">
                <option value="normal">{{ t('quarantine.timeline.breathing.normal').value }}</option>
                <option value="fast">{{ t('quarantine.timeline.breathing.fast').value }}</option>
                <option value="labored">{{ t('quarantine.timeline.breathing.labored').value }}</option>
              </select>
            </div>
          </div>
          
          <div class="input-group">
            <label>{{ t('quarantine.timeline.stepDetail.symptomsLabel').value }}</label>
            <div class="symptoms-checkboxes">
              <label
                v-for="symptom in availableSymptoms"
                :key="symptom.value"
                class="checkbox-label"
              >
                <input
                  type="checkbox"
                  :value="symptom.value"
                  v-model="fishStatus.symptoms"
                >
                <span class="checkbox-text">{{ symptom.label }}</span>
              </label>
            </div>
          </div>
          
          <div class="input-group">
            <label>备注</label>
            <textarea
              v-model="fishStatus.notes"
              class="form-control"
              :placeholder="t('quarantine.timeline.stepDetail.notesPlaceholder').value"
              rows="3"
            ></textarea>
          </div>
        </div>

        <!-- 安全提示 -->
        <div class="safety-tips">
          <h4>{{ t('quarantine.timeline.stepDetail.safetyTips').value }}</h4>
          <ul class="tips-list">
            <li v-if="hasFormalin">{{ t('quarantine.timeline.stepDetail.safetyTipItems.formalin1').value }}</li>
            <li v-if="hasFormalin">{{ t('quarantine.timeline.stepDetail.safetyTipItems.formalin2').value }}</li>
            <li v-if="hasCopperOrChloroquine">{{ t('quarantine.timeline.stepDetail.safetyTipItems.copperOrChloroquine').value }}</li>
            <li v-if="hasPraziquantel">{{ t('quarantine.timeline.stepDetail.safetyTipItems.praziquantel1').value }}</li>
            <li v-if="hasPraziquantel">{{ t('quarantine.timeline.stepDetail.safetyTipItems.praziquantel2').value }}</li>
            <li>{{ t('quarantine.timeline.stepDetail.safetyTipItems.observe').value }}</li>
            <li>{{ t('quarantine.timeline.stepDetail.safetyTipItems.record').value }}</li>
          </ul>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-secondary" @click="closeModal">
          {{ t('quarantine.timeline.stepDetail.cancel').value }}
        </button>
        <button
          class="btn-primary"
          @click="completeStep"
          :disabled="!canComplete"
        >
          {{ t('quarantine.timeline.stepDetail.markComplete').value }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from '../composables/useI18n'
import {
  type QuarantineStep,
  type FishStatus,
  type DrugDose,
  MEDICATION_SPECS,
  calculateMedicationDosage
} from '../composables/useQuarantinePlan'

// Props
interface Props {
  show: boolean
  step: QuarantineStep | null
  waterVolume: number
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  step: null,
  waterVolume: 0
})

// Emits
const emit = defineEmits<{
  'update:show': [value: boolean]
  'complete': [step: QuarantineStep | null, fishStatus: FishStatus]
}>()

const { t } = useI18n()

// 响应式数据
const waterVolume = ref<number>(props.waterVolume || 20) // 默认20L
const medicationSpecs = ref<Record<string, string>>({})
const customConcentrations = ref<Record<string, number>>({})
const fishStatus = ref<FishStatus>({
  appetite: 'normal',
  breathing: 'normal',
  symptoms: [],
  notes: ''
})

// 可用症状列表
const availableSymptoms = ref([
  { value: '白点', label: t('quarantine.timeline.symptoms.whiteSpot').value },
  { value: '白膜', label: t('quarantine.timeline.symptoms.whiteFilm').value },
  { value: '红斑', label: t('quarantine.timeline.symptoms.redSpot').value },
  { value: '飞碟虫', label: t('quarantine.timeline.symptoms.discusWorm').value },
  { value: '擦身', label: t('quarantine.timeline.symptoms.scratching').value },
  { value: '缩鳍', label: t('quarantine.timeline.symptoms.finClamp').value },
  { value: '蒙眼', label: t('quarantine.timeline.symptoms.cloudyEye').value },
  { value: '烂鳍', label: t('quarantine.timeline.symptoms.finRot').value },
  { value: '腹胀', label: t('quarantine.timeline.symptoms.bloating').value },
  { value: '瘦弱', label: t('quarantine.timeline.symptoms.emaciation').value }
])

// 计算属性
const hasFormalin = computed(() => {
  return props.step?.drugs?.some((drug: any) => drug.name === 'formalin') || false
})

const hasCopperOrChloroquine = computed(() => {
  return props.step?.drugs?.some((drug: any) =>
    drug.name === 'copper' || drug.name === 'chloroquine-phosphate'
  ) || false
})

const hasPraziquantel = computed(() => {
  return props.step?.drugs?.some((drug: any) => drug.name === 'praziquantel') || false
})

const canComplete = computed(() => {
  // 对于观察期等不需要水体的步骤，可以直接完成
  if (props.step?.type === 'observation' || props.step?.type === 'maintenance') {
    return true
  }
  // 对于需要用药的步骤，需要水体体积
  return waterVolume.value > 0
})

// 方法
const getStepTypeLabel = (type: string) => {
  const typeLabels: Record<string, string> = {
    'medication': '用药',
    'water_change': '换水',
    'observation': '观察',
    'feeding': '喂食',
    'transfer': '换桶'
  }
  return typeLabels[type] || type
}

const getMedicationName = (medKey: string) => {
  return MEDICATION_SPECS[medKey as keyof typeof MEDICATION_SPECS]?.name || medKey
}


const getMedicationUnit = (medKey: string) => {
  return MEDICATION_SPECS[medKey as keyof typeof MEDICATION_SPECS]?.unit || '未知'
}

const calculateDosage = (drug: DrugDose) => {
  if (waterVolume.value <= 0) return 0
  
  let dosage = 0
  
  if (drug.dose_ml_per_L) {
    // ml/L 计算：直接乘以水体体积
    dosage = drug.dose_ml_per_L * waterVolume.value
  } else if (drug.dose_ml_per_10L) {
    // ml/10L 计算：先转换为每升，再乘以水体体积
    dosage = (drug.dose_ml_per_10L / 10) * waterVolume.value
  } else if (drug.dose_ml_per_20L) {
    // ml/20L 计算：先转换为每升，再乘以水体体积
    dosage = (drug.dose_ml_per_20L / 20) * waterVolume.value
  } else if (drug.dose_ml_per_75L) {
    // ml/75L 计算：先转换为每升，再乘以水体体积
    dosage = (drug.dose_ml_per_75L / 75) * waterVolume.value
  } else if (drug.dose_mg_per_L) {
    // mg/L 计算：直接乘以水体体积
    dosage = drug.dose_mg_per_L * waterVolume.value
  } else if (drug.targetPPM) {
    // PPM 计算：使用专门的函数
    const result = calculateMedicationDosage(drug.name, drug.targetPPM, waterVolume.value)
    return result.dosage
  } else if (drug.tabletPerX_L) {
    // 片剂计算：向上取整，确保剂量充足
    const [tablets, volume] = drug.tabletPerX_L
    dosage = Math.ceil(waterVolume.value / volume * tablets)
  }
  
  // 对于液体药物，保留2位小数；对于片剂，返回整数
  if (drug.tabletPerX_L) {
    return dosage
  } else {
    return Math.round(dosage * 100) / 100 // 保留2位小数
  }
}

const getCalculationNote = (drug: DrugDose) => {
  let note = ''
  
  if (drug.dose_ml_per_L) {
    note = `每升水添加 ${drug.dose_ml_per_L}ml (${drug.dose_ml_per_L}ml/L × ${waterVolume.value}L)`
  } else if (drug.dose_ml_per_10L) {
    const perLiter = drug.dose_ml_per_10L / 10
    note = `每10升水添加 ${drug.dose_ml_per_10L}ml (${perLiter.toFixed(3)}ml/L × ${waterVolume.value}L)`
  } else if (drug.dose_ml_per_20L) {
    const perLiter = drug.dose_ml_per_20L / 20
    note = `每20升水添加 ${drug.dose_ml_per_20L}ml (${perLiter.toFixed(3)}ml/L × ${waterVolume.value}L)`
  } else if (drug.dose_ml_per_75L) {
    const perLiter = drug.dose_ml_per_75L / 75
    note = `每75升水添加 ${drug.dose_ml_per_75L}ml (${perLiter.toFixed(4)}ml/L × ${waterVolume.value}L)`
  } else if (drug.dose_mg_per_L) {
    note = `每升水添加 ${drug.dose_mg_per_L}mg (${drug.dose_mg_per_L}mg/L × ${waterVolume.value}L)`
  } else if (drug.targetPPM) {
    const result = calculateMedicationDosage(drug.name, drug.targetPPM, waterVolume.value)
    note = `目标浓度: ${drug.targetPPM}ppm | ${result.notes}`
  } else if (drug.tabletPerX_L) {
    const [tablets, volume] = drug.tabletPerX_L
    note = `每${volume}升水使用${tablets}片 (${waterVolume.value}L ÷ ${volume}L × ${tablets}片)`
  }
  
  // 添加安全提示
  if (drug.name === 'formalin') {
    note += ' | ⚠️ 必须分两次加药，间隔30分钟，全程打氧！'
  } else if (drug.name === 'chloroquine-phosphate' || drug.name === 'copper') {
    note += ' | ⚠️ 禁用硝化滤材，需通过换水维持药物浓度'
  } else if (drug.name === 'praziquantel') {
    note += ' | ⚠️ 需停用UV和活性炭，可能导致水体浑浊'
  }
  
  return note
}

const closeModal = () => {
  emit('update:show', false)
}

const completeStep = () => {
  if (canComplete.value) {
    emit('complete', props.step, fishStatus.value)
    closeModal()
  }
}

// 监听props变化
watch(() => props.waterVolume, (newValue) => {
  waterVolume.value = newValue || 20 // 如果没有传入值，使用默认20L
})

watch(() => props.show, (newValue) => {
  if (newValue) {
    // 如果有已保存的鱼只状态，则加载；否则重置
    if (props.step?.fishStatus) {
      fishStatus.value = {
        appetite: props.step.fishStatus.appetite || 'normal',
        breathing: props.step.fishStatus.breathing || 'normal',
        symptoms: props.step.fishStatus.symptoms || [],
        notes: props.step.fishStatus.notes || ''
      }
    } else {
      // 重置表单状态
      fishStatus.value = {
        appetite: 'normal',
        breathing: 'normal',
        symptoms: [],
        notes: ''
      }
    }
    
    // 初始化药品规格
    if (props.step?.drugs) {
      props.step.drugs.forEach((drug: any) => {
        medicationSpecs.value[drug.name] = 'default'
        customConcentrations.value[drug.name] = MEDICATION_SPECS[drug.name as keyof typeof MEDICATION_SPECS]?.concentration || 1
      })
    }
  }
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-container {
  background: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.1);
  max-width: 90vw;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background: #ffffff;
  border-radius: 8px 8px 0 0;
}

.modal-header h3 {
  color: #333333;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666666;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: #e9ecef;
  color: #333333;
}

.modal-content {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
}

.modal-footer {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e9ecef;
  background: #ffffff;
  border-radius: 0 0 8px 8px;
}

.btn-primary, .btn-secondary {
  flex: 1;
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  background: transparent;
  color: #666666;
  border: 1px solid #e9ecef;
}

.btn-secondary:hover {
  background: #e9ecef;
  color: #333333;
}

/* 步骤信息样式 */
.step-info-section {
  margin-bottom: 2rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-label {
  color: #666666;
  font-size: 0.875rem;
  font-weight: 500;
}

.info-value {
  color: #333333;
  font-size: 1rem;
}

/* 药品卡片样式 */
.medications-section {
  margin-bottom: 2rem;
}

.medications-section h4 {
  color: #333333;
  margin-bottom: 1rem;
}

.medication-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.medication-card {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1.5rem;
}

.med-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.med-header h5 {
  color: #333333;
  margin: 0;
}

.target-ppm {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
}

.dosage-calculation {
  margin-bottom: 1rem;
}

.calculation-inputs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.input-group label {
  color: #666666;
  font-size: 0.875rem;
  font-weight: 500;
}

.custom-concentration {
  margin-bottom: 1rem;
}

.custom-concentration label {
  display: block;
  color: #666666;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.calculation-result {
  padding: 1rem;
  background: rgba(102, 126, 234, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(102, 126, 234, 0.2);
}

.result-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.result-label {
  color: #666666;
  font-weight: 500;
}

.result-value {
  color: #667eea;
  font-weight: 700;
  font-size: 1.125rem;
}

.calculation-note {
  color: #666666;
  font-size: 0.875rem;
  font-style: italic;
}

.med-notes {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem;
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 6px;
}

.notes-icon {
  font-size: 1.125rem;
  line-height: 1;
}

.notes-text {
  color: #ffc107;
  font-size: 0.875rem;
  line-height: 1.4;
  flex: 1;
}

/* 必做操作样式 */
.actions-section {
  margin-bottom: 2rem;
}

.section-title {
  color: #2c3e50;
  margin-bottom: 1.5rem;
  font-size: 1.25rem;
  font-weight: 600;
  border-bottom: 2px solid #3498db;
  padding-bottom: 0.5rem;
}

.actions-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.action-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 2px solid #e8f5e8;
  border-radius: 12px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.1);
}

.action-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(40, 167, 69, 0.15);
  border-color: #28a745;
}

.action-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  border-radius: 50%;
  font-weight: 700;
  font-size: 0.875rem;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);
}

.action-content {
  flex: 1;
  display: flex;
  align-items: center;
  min-height: 32px;
}

.action-text {
  color: #2c3e50;
  line-height: 1.6;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.01em;
}

/* 水体体积警告样式 */
.volume-warning {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: rgba(220, 53, 69, 0.1);
  border-radius: 4px;
  border: 1px solid rgba(220, 53, 69, 0.2);
}

/* 警告信息样式 */
.warnings-section {
  margin-bottom: 2rem;
}

.warnings-section h4 {
  color: #333333;
  margin-bottom: 1rem;
}

.warning-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.warning-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 6px;
}

.warning-icon {
  font-size: 1.125rem;
  line-height: 1;
  flex-shrink: 0;
}

.warning-text {
  color: #ffc107;
  line-height: 1.4;
  flex: 1;
}


/* 安全提示样式 */
.safety-tips {
  margin-bottom: 2rem;
}

.safety-tips h4 {
  color: #333333;
  margin-bottom: 1rem;
}

.tips-list {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1.5rem;
  list-style: none;
}

.tips-list li {
  color: #666666;
  margin-bottom: 0.75rem;
  padding-left: 1.5rem;
  position: relative;
  line-height: 1.5;
}

.tips-list li:before {
  content: "•";
  color: #667eea;
  position: absolute;
  left: 0.5rem;
}

.tips-list li:last-child {
  margin-bottom: 0;
}

/* 表单控件样式 */
.form-control {
  padding: 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: #f8f9fa;
  color: #333333;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

.select-control {
  padding: 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: #f8f9fa;
  color: #333333;
  font-size: 1rem;
  transition: all 0.3s ease;
  cursor: pointer;
}

.select-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

/* 鱼只状态记录样式 */
.fish-status-section {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.fish-status-section h4 {
  color: #333333;
  margin-bottom: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.symptoms-checkboxes {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

.checkbox-label:hover {
  background: rgba(102, 126, 234, 0.05);
}

.checkbox-label input[type="checkbox"] {
  margin: 0;
}

.checkbox-text {
  color: #333333;
  font-size: 0.875rem;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .modal-container {
    margin: 0.5rem;
    max-height: calc(100vh - 10rem);
  }
  
  .modal-content {
    padding: 1.25rem;
  }
  
  .modal-footer {
    padding: 1.25rem;
    flex-direction: column;
  }
  
  .calculation-inputs {
    grid-template-columns: 1fr;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .symptoms-checkboxes {
    grid-template-columns: 1fr;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
}
</style>
